import React from 'react'
import { Dropdown as AntdDropdown } from 'antd'

import { SelectStyleWrapper } from './SelectStyleWrapper'

/**
 * @typedef {Object} DropdownOption
 * @property {string|number} value - Option value
 * @property {string} label - Option label text
 * @property {React.ComponentType} [Icon] - Icon component for left side
 * @property {boolean} [disabled] - Whether option is disabled
 * @property {React.ReactNode} [preLabel] - Pre-label content
 */

/**
 * @typedef {Object} DropdownProps
 * @property {React.ReactNode} children - Trigger element (IconButton, Button, etc.)
 * @property {DropdownOption[]} [options=[]] - Dropdown menu options
 * @property {Function} [onSelect] - Callback when option is selected
 * @property {string|number} [selectedValue] - Currently selected value for highlighting
 * @property {string} [placement='bottomLeft'] - Dropdown placement
 * @property {string[]} [trigger=['click']] - Trigger method
 * @property {boolean} [disabled=false] - Whether dropdown is disabled
 * @property {string} [className] - Additional CSS class
 * @property {Object} [dropdownStyle] - Custom dropdown styles
 * @property {Function} [getPopupContainer] - Custom popup container
 */

/**
 * Dropdown component with Select-like styling
 * @param {DropdownProps} props - The component props
 * @returns {JSX.Element} The Dropdown component
 */
const Dropdown = ({
  children,
  options = [],
  onSelect,
  selectedValue,
  placement = 'bottomLeft',
  trigger = ['click'],
  disabled = false,
  className,
  dropdownStyle = {},
  getPopupContainer,
  ...props
}) => {
  const menuItems = options.map((option) => ({
    key: option.value || option.key,
    label: (
      <div className="option" title={option.label}>
        {option.Icon && <option.Icon />}
        {option.preLabel && <span className="pre-label">{option.preLabel}</span>}
        <div
          className="option_label typography-body-1"
          id={option.id || option.value || option.key}
        >
          {option.label}
        </div>
      </div>
    ),
    disabled: option.disabled,
    danger: option.danger,
    onClick: () => onSelect?.(option.value, option),
  }))

  return (
    <AntdDropdown
      menu={{
        items: menuItems,
        selectedKeys: selectedValue ? [selectedValue] : [],
        style: {
          padding: '8px',
          background: 'var(--color-elevated)',
          color: 'var(--color-text)',
          boxShadow: 'var(--color-elevated-shadow)',
          border: '1px solid var(--color-elevated-border)',
          ...dropdownStyle,
        },
      }}
      placement={placement}
      trigger={trigger}
      disabled={disabled}
      getPopupContainer={getPopupContainer}
      popupRender={(menu) => (
        <SelectStyleWrapper className={`select-dropdown ${className || ''}`}>
          {menu}
        </SelectStyleWrapper>
      )}
      {...props}
    >
      {children}
    </AntdDropdown>
  )
}

export default Dropdown
